<script setup lang="ts">
/**
 * The principal risks, as a management report sets them out.
 *
 * Grouped by family, in the order the families first appear, because that is
 * the order the author chose and the regulator asks for the most significant
 * to come first. Within a family, each risk is a short passage rather than a
 * row: what could happen, then what is done about it. Those are paragraphs,
 * and a paragraph in a table cell is a paragraph nobody reads.
 *
 * The family names are set as labels, not as headings. A heading here would
 * join the document's outline and the contents page, and a contents page that
 * lists "Risques opérationnels" beside "Comptes consolidés" has confused a
 * label with a chapter.
 *
 * The severity is written out as well as marked. A mark alone is a colour, and
 * colour does not survive the photocopier this is still read from.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { RiskEntry, RisksSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: RisksSection }>()

interface Family {
  readonly key: string
  readonly name: string
  readonly risks: readonly RiskEntry[]
}

/** A risk without a name is one still being written, and is not printed. */
const families = computed<Family[]>(() => {
  const order: string[] = []
  const byName = new Map<string, RiskEntry[]>()

  for (const entry of props.section.entries) {
    if (entry.title.trim() === '') continue
    const name = entry.category.trim()
    const held = byName.get(name)
    if (held) {
      held.push(entry)
    } else {
      byName.set(name, [entry])
      order.push(name)
    }
  }

  return order.map((name, at) => ({
    key: `${at}:${name}`,
    name: name || t('inspector.risks.uncategorised'),
    risks: byName.get(name) ?? [],
  }))
})
</script>

<template>
  <figure class="risks">
    <section v-for="family in families" :key="family.key" class="risks__family">
      <p class="risks__category">{{ family.name }}</p>

      <article v-for="risk in family.risks" :key="risk.id" class="risk">
        <p class="risk__head">
          <span class="risk__title">{{ risk.title }}</span>
          <span class="risk__level" :class="`risk__level--${risk.level}`">
            {{ t(`inspector.risks.levels.${risk.level}`) }}
          </span>
        </p>

        <p v-if="risk.description" class="risk__part">
          <span class="risk__label">{{ t('inspector.risks.description') }}</span>
          {{ risk.description }}
        </p>

        <p v-if="risk.management" class="risk__part">
          <span class="risk__label">{{ t('inspector.risks.management') }}</span>
          {{ risk.management }}
        </p>
      </article>
    </section>

    <p v-if="families.length === 0" class="risks__empty">{{ t('inspector.risks.empty') }}</p>

    <figcaption v-if="section.caption" class="page-caption">{{ section.caption }}</figcaption>
  </figure>
</template>

<style scoped>
.risks {
  margin: 0;
}

.risks__family + .risks__family {
  margin-top: 5mm;
}

.risks__category {
  margin: 0 0 2.5mm;
  padding-bottom: 1mm;
  border-bottom: 0.3mm solid var(--page-accent);
  color: var(--page-accent);
  font-size: 8.5pt;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
}

.risk {
  /* A risk read in two halves across a page break loses the half that says
     what is done about it. */
  break-inside: avoid;
}

.risk + .risk {
  margin-top: 3.5mm;
}

.risk__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 4mm;
  margin: 0 0 1mm;
  text-align: left;
}

.risk__title {
  font-weight: 600;
}

.risk__level {
  display: inline-flex;
  align-items: center;
  gap: 1.2mm;
  flex: none;
  color: var(--paper-ink-soft);
  font-size: 8pt;
  white-space: nowrap;
}

/* Filled for high, half for moderate, hollow for low: readable in black. */
.risk__level::before {
  content: '';
  width: 2mm;
  height: 2mm;
  border-radius: 50%;
  border: 0.35mm solid var(--page-accent);
}

.risk__level--high::before {
  background: var(--page-accent);
}

.risk__level--medium::before {
  background: linear-gradient(90deg, var(--page-accent) 50%, transparent 50%);
}

.risk__part {
  margin: 0 0 1mm;
}

.risk__label {
  margin-right: 1.5mm;
  color: var(--paper-ink-soft);
  font-size: 8.5pt;
  font-weight: 600;
}

.risks__empty {
  margin: 2mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 9pt;
  font-style: italic;
  text-align: left;
}

@media print {
  .risk__level::before {
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
}
</style>
